'use client';

import {
  forwardRef,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { automationId } from '@terra/shared-utils';
import { ArrowDown01Icon, Cancel01Icon } from '@terra/ui-icons';
import {
  Box,
  styled,
  token,
  type HTMLStyledProps,
} from '@terra/ui-styled-system';
import { Text } from './text';
import { Tooltip } from './tooltip';

export type FilterDensity = 'default' | 'compact';

const FILTER_DENSITY_CONFIG = {
  default: {
    height: '32px',
    paddingBlock: token('spacing.2'),
    paddingInline: `${token('spacing.3')} ${token('spacing.9')}`,
    fontSize: 'sm',
    indicatorRight: '3',
    clearButtonSize: '20px',
    clearIconSize: 14,
  },
  compact: {
    height: '30px',
    paddingBlock: token('spacing.1'),
    paddingInline: `${token('spacing.2')} ${token('spacing.7')}`,
    fontSize: 'xs',
    indicatorRight: '2',
    clearButtonSize: '18px',
    clearIconSize: 12,
  },
} as const satisfies Record<FilterDensity, Record<string, string | number>>;

const getFilterStyles = ({
  disabled,
  isActive,
  readOnly,
  density,
}: {
  disabled: boolean;
  isActive: boolean;
  readOnly: boolean;
  density: FilterDensity;
}) => {
  const densityConfig = FILTER_DENSITY_CONFIG[density];
  const baseStyles = {
    height: densityConfig.height,
    paddingBlock: densityConfig.paddingBlock,
    paddingInline: densityConfig.paddingInline,
    borderRadius: '16px',
    borderWidth: '1px',
    borderStyle: 'solid',
    cursor: readOnly ? 'default' : 'pointer',
    transition: 'all 0.2s',
    width: isActive ? '100%' : 'auto',
  };
  const defaultStyles = {
    ...baseStyles,
    backgroundColor: token('colors.bg.neutral.default'),
    borderColor: token('colors.border.neutral.subtle'),
  };
  const activeStyles = {
    ...baseStyles,
    backgroundColor: token('colors.bg.neutral.default'),
    borderColor: token('colors.border.accent.default'),
  };
  const disabledStyles = {
    ...baseStyles,
    backgroundColor: token('colors.border.neutral.disabled'),
    borderColor: token('colors.border.neutral.disabled'),
    cursor: 'not-allowed',
  };

  return disabled ? disabledStyles : isActive ? activeStyles : defaultStyles;
};

const getTextColor = ({
  disabled,
  isActive,
}: {
  disabled: boolean;
  isActive: boolean;
}) => {
  if (disabled) {
    return token('colors.fg.neutral.disabled');
  }

  if (isActive) {
    return token('colors.fg.neutral.muted');
  }

  return token('colors.fg.neutral.subtle');
};

export type FilterPillProps = {
  placeholder: string;
  isActive: boolean;
  selectedText?: ReactNode;
  density?: FilterDensity;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  tooltip?: ReactNode;
  dataTestid?: string;
  onClear: (event: MouseEvent | KeyboardEvent) => void;
} & Omit<HTMLStyledProps<'div'>, 'children' | 'placeholder'>;

export const FilterPill = forwardRef<HTMLDivElement, FilterPillProps>(
  function FilterPill(
    {
      placeholder,
      isActive,
      selectedText,
      density = 'default',
      disabled = false,
      readOnly = false,
      required = false,
      tooltip,
      dataTestid = '',
      onClear,
      ...rootProps
    },
    ref,
  ) {
    const densityConfig = FILTER_DENSITY_CONFIG[density];
    const triggerButton = (
      <styled.button
        type="button"
        disabled={disabled}
        aria-disabled={disabled || readOnly}
        {...automationId(dataTestid)}
        style={getFilterStyles({ disabled, isActive, readOnly, density })}
        display="flex"
        alignItems="center"
        overflow={isActive ? 'hidden' : 'visible'}
        minW={isActive ? '0' : undefined}
        _hover={
          disabled || readOnly
            ? {}
            : {
                bg: 'bg.neutral.subtleSoft',
                color: 'fg.neutral.muted',
              }
        }
        textAlign="left"
      >
        <Text
          fontSize={densityConfig.fontSize}
          fontWeight="normal"
          whiteSpace="nowrap"
          truncate={isActive}
          minW={isActive ? '0' : undefined}
          flex={isActive ? '1' : undefined}
          style={{ color: getTextColor({ disabled, isActive }) }}
        >
          {isActive ? (
            <>
              {placeholder}{' '}
              <Text
                as="span"
                color={disabled ? 'fg.neutral.disabled' : 'fg.neutral.default'}
              >
                {selectedText}
              </Text>
            </>
          ) : (
            placeholder
          )}
        </Text>
      </styled.button>
    );

    return (
      <Box
        ref={ref}
        position="relative"
        flexShrink={isActive ? 1 : 0}
        minW={isActive ? '0' : 'max-content'}
        overflow={isActive ? 'hidden' : 'visible'}
        {...rootProps}
      >
        {tooltip ? (
          <Tooltip
            title={typeof tooltip === 'string' ? tooltip : undefined}
            customContent={typeof tooltip === 'string' ? undefined : tooltip}
            whiteSpace="pre-line"
            positioning={{ strategy: 'fixed', placement: 'top' }}
          >
            {triggerButton}
          </Tooltip>
        ) : (
          triggerButton
        )}
        <Box
          position="absolute"
          top="50%"
          right={densityConfig.indicatorRight}
          transform="translateY(-50%)"
        >
          {isActive && !required ? (
            <styled.button
              type="button"
              disabled={disabled}
              aria-disabled={disabled}
              display="flex"
              alignItems="center"
              justifyContent="center"
              borderRadius="full"
              w={densityConfig.clearButtonSize}
              h={densityConfig.clearButtonSize}
              bg="bg.neutral.subtle"
              color="fg.default"
              cursor={disabled ? 'not-allowed' : 'pointer'}
              opacity={disabled ? 0.5 : 1}
              _hover={disabled ? {} : { bg: 'bg.neutral.subtleSoft' }}
              aria-label="Clear filter"
              onClick={onClear}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  onClear(event);
                }
              }}
            >
              <Cancel01Icon
                size={densityConfig.clearIconSize}
                color={token('colors.fg.default')}
              />
            </styled.button>
          ) : (
            <ArrowDown01Icon
              size={16}
              color={token(
                disabled
                  ? 'colors.fg.neutral.disabled'
                  : 'colors.fg.neutral.subtle',
              )}
            />
          )}
        </Box>
      </Box>
    );
  },
);
